import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import motionify from "../../components/Motion";
import { Form, FormikProvider, useFormik } from "formik";
import { useAppStore } from "../../shared/stores/useAppStore";
import * as Yup from "yup";
import TailwindInput from "../../components/Tailwind/TailwindInput";
import TailwindSelect from "../../components/Tailwind/TailwindSelect";
import TailwindButton from "../../components/Tailwind/TailwindButton";
import { cn } from "../../lib/utils";
import { useUserPreferences } from "../../shared/hooks/queries/useUserPreferences";
import { useUpdateUserPreferences } from "../../shared/hooks/mutations/useUserPreferencesMutations";
import {
  CHART_PRIMARY,
  generatePaletteFromPrimary,
  isValidHexColor,
} from "../../components/Tailwind/chartColors";

const DATE_SYSTEM_ENUM = {
    'dd/mm/yyyy': 'DMY',
    'mm/dd/yyyy': 'MDY',
}

const DATESYSTEM_TO_FORMAT = {
    'DMY': 'DD/MM/YYYY',
    'MDY': 'MM/DD/YYYY',
}

const LOCALE_ENUM = ['en-US', 'fr-FR', 'de-DE', 'es-ES', 'ja-JP']

const LOCALE_LABELS = {
    'en-US': 'English (US)',
    'fr-FR': 'French (FR)',
    'de-DE': 'German (DE)',
    'es-ES': 'Spanish (ES)',
    'ja-JP': 'Japanese (JP)',
}

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const formatNumberPreview = (locale: string, precision: number) => {
    try {
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
        }).format(1234567.89);
    } catch {
        return '1,234,567.89';
    }
};

export default function Settings({ entity }) {
    entity;

    const { data: prefs, isLoading } = useUserPreferences();
    const updatePreferences = useUpdateUserPreferences();

    const [snackBarOpen, setSnackBarOpen] = useState(false);

    // Auto-dismiss snackbar
    useEffect(() => {
        if (snackBarOpen) {
            const timer = setTimeout(() => setSnackBarOpen(false), 4000);
            return () => clearTimeout(timer);
        }
    }, [snackBarOpen]);

    const SettingsSchema = Yup.object().shape({
        dateSystem: Yup.string(),
        roundPrecision: Yup.number().required().integer().min(0).max(20),
        numberFormattingLocale: Yup.string(),
        chartPrimaryColor: Yup.string()
            .nullable()
            .matches(HEX_COLOR_REGEX, { message: 'Must be a #RRGGBB hex color', excludeEmptyString: true }),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            dateSystem: prefs?.date_system ?? 'DMY',
            roundPrecision: Number.isFinite(prefs?.round_precision) ? prefs!.round_precision : 2,
            numberFormattingLocale: prefs?.number_formatting_locale ?? 'fr-FR',
            chartPrimaryColor: prefs?.chart_primary_color ?? '',
        },
        validationSchema: SettingsSchema,
        onSubmit: () => {
            const normalizedColor = isValidHexColor(values.chartPrimaryColor) ? values.chartPrimaryColor : null;
            updatePreferences.mutate(
                {
                    dateSystem: values.dateSystem,
                    roundPrecision: values.roundPrecision,
                    numberFormattingLocale: values.numberFormattingLocale,
                    chartPrimaryColor: normalizedColor,
                },
                {
                    onSuccess: () => {
                        useAppStore.getState().setUserSettings({
                            dateSystem: values.dateSystem,
                            roundPrecision: values.roundPrecision,
                            numberFormattingLocale: values.numberFormattingLocale,
                            chartPrimaryColor: normalizedColor,
                        });
                        setSnackBarOpen(true);
                        setSubmitting(false);
                    },
                    onError: () => {
                        setSubmitting(false);
                    },
                },
            );
        },
    });

    const { errors, values, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setSubmitting } = formik;

    const safePrecision = Number.isFinite(Number(values.roundPrecision))
        ? Math.max(0, Math.min(20, Math.floor(Number(values.roundPrecision))))
        : 2;

    const dateSystemOptions = Object.keys(DATE_SYSTEM_ENUM).map(key => ({
        value: DATE_SYSTEM_ENUM[key],
        label: key.toUpperCase()
    }));

    const localeOptions = LOCALE_ENUM.map(locale => ({
        value: locale,
        label: LOCALE_LABELS[locale] || locale,
    }));

    const effectiveChartColor = isValidHexColor(values.chartPrimaryColor)
        ? values.chartPrimaryColor
        : CHART_PRIMARY;
    const chartPalettePreview = generatePaletteFromPrimary(effectiveChartColor, 6);

    return (<>
        {motionify(
            <div className="flex-grow bg-white min-h-full">
                {/* Page Header */}
                <div className="px-8 py-6 border-b border-slate-100 bg-white sticky top-0 z-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                        <div className="flex-1">
                            <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                                Settings
                            </h2>
                            <p className="text-sm text-slate-500 mt-2">
                                Configure display preferences for dates, numbers, and formatting
                            </p>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="px-8 py-6">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-primary" />
                        </div>
                    ) : (
                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                            <div className="border border-slate-100 rounded-xl overflow-hidden shadow-sm">
                                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[20px] text-slate-600">tune</span>
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Display Preferences</h3>
                                </div>

                                <div className="p-6">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                        {/* Date Format */}
                                        <div className="flex flex-col gap-3">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[18px] text-slate-400">calendar_today</span>
                                                <span className="text-sm font-semibold text-slate-900">Date Format</span>
                                            </div>
                                            <p className="text-[11px] text-slate-400 -mt-1">
                                                Choose how dates are displayed across the application
                                            </p>
                                            <TailwindSelect
                                                value={values.dateSystem}
                                                onChange={(value) => setFieldValue('dateSystem', value)}
                                                options={dateSystemOptions}
                                                placeholder="Select date format"
                                            />
                                            <div className="flex items-center gap-2 mt-1 px-1">
                                                <span className="text-[11px] text-slate-400">Preview:</span>
                                                <span className="text-[11px] font-mono font-medium text-slate-600 bg-slate-50 px-2 py-0.5 rounded">
                                                    {DATESYSTEM_TO_FORMAT[values.dateSystem] || 'DD/MM/YYYY'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Round Precision */}
                                        <div className="flex flex-col gap-3">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[18px] text-slate-400">decimal_increase</span>
                                                <span className="text-sm font-semibold text-slate-900">Round Precision</span>
                                            </div>
                                            <p className="text-[11px] text-slate-400 -mt-1">
                                                Number of decimal places for computed values
                                            </p>
                                            <TailwindInput
                                                type="number"
                                                name="roundPrecision"
                                                value={values.roundPrecision}
                                                onBlur={getFieldProps("roundPrecision").onBlur}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (raw === '') {
                                                        setFieldValue('roundPrecision', '');
                                                        return;
                                                    }
                                                    const parsed = parseInt(raw, 10);
                                                    if (!isNaN(parsed)) {
                                                        setFieldValue('roundPrecision', Math.max(0, Math.min(20, parsed)));
                                                    }
                                                }}
                                                min={0}
                                                max={20}
                                                step={1}
                                                error={errors.roundPrecision ? "Must be an integer between 0 and 20" : undefined}
                                            />
                                        </div>

                                        {/* Number Formatting Locale */}
                                        <div className="flex flex-col gap-3">
                                            <div className="flex items-center gap-2">
                                                <span className="material-symbols-outlined text-[18px] text-slate-400">language</span>
                                                <span className="text-sm font-semibold text-slate-900">Number Formatting</span>
                                            </div>
                                            <p className="text-[11px] text-slate-400 -mt-1">
                                                Locale used for number separators and formatting
                                            </p>
                                            <TailwindSelect
                                                value={values.numberFormattingLocale}
                                                onChange={(value) => setFieldValue('numberFormattingLocale', value)}
                                                options={localeOptions}
                                                placeholder="Select locale"
                                            />
                                            <div className="flex items-center gap-2 mt-1 px-1">
                                                <span className="text-[11px] text-slate-400">Preview:</span>
                                                <span className="text-[11px] font-mono font-medium text-slate-600 bg-slate-50 px-2 py-0.5 rounded">
                                                    {formatNumberPreview(values.numberFormattingLocale, safePrecision)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Chart Appearance section */}
                                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[20px] text-slate-600">palette</span>
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Chart Appearance</h3>
                                </div>

                                <div className="p-6">
                                    <div className="flex flex-col gap-3 max-w-xl">
                                        <div className="flex items-center gap-2">
                                            <span className="material-symbols-outlined text-[18px] text-slate-400">colorize</span>
                                            <span className="text-sm font-semibold text-slate-900">Primary Color</span>
                                        </div>
                                        <p className="text-[11px] text-slate-400 -mt-1">
                                            Drives the primary color of every chart. Other series colors are auto-generated.
                                        </p>

                                        <div className="flex items-center gap-3">
                                            <input
                                                type="color"
                                                aria-label="Chart primary color"
                                                value={effectiveChartColor}
                                                onChange={(e) => setFieldValue('chartPrimaryColor', e.target.value)}
                                                className="h-10 w-12 rounded-lg border border-slate-200 bg-white cursor-pointer p-0"
                                            />
                                            <TailwindInput
                                                type="text"
                                                name="chartPrimaryColor"
                                                value={values.chartPrimaryColor}
                                                onChange={(e) => setFieldValue('chartPrimaryColor', e.target.value)}
                                                placeholder="#135bec"
                                                error={
                                                    values.chartPrimaryColor && !HEX_COLOR_REGEX.test(values.chartPrimaryColor)
                                                        ? 'Must be a #RRGGBB hex color'
                                                        : undefined
                                                }
                                            />
                                            <TailwindButton
                                                type="button"
                                                variant="ghost"
                                                icon="restart_alt"
                                                size="md"
                                                disabled={!values.chartPrimaryColor}
                                                onClick={() => setFieldValue('chartPrimaryColor', '')}
                                            >
                                                Reset
                                            </TailwindButton>
                                        </div>

                                        <div className="flex items-center gap-2 mt-1 px-1">
                                            <span className="text-[11px] text-slate-400">Preview:</span>
                                            <div className="flex items-center gap-1">
                                                {chartPalettePreview.map((color, idx) => (
                                                    <span
                                                        key={`${color}-${idx}`}
                                                        title={color}
                                                        className="inline-block h-4 w-4 rounded-full border border-white shadow-sm"
                                                        style={{ backgroundColor: color }}
                                                    />
                                                ))}
                                            </div>
                                            <span className="text-[11px] font-mono font-medium text-slate-600 bg-slate-50 px-2 py-0.5 rounded">
                                                {values.chartPrimaryColor ? effectiveChartColor : `${CHART_PRIMARY} (default)`}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-end">
                                    <TailwindButton
                                        type="submit"
                                        variant="dark"
                                        icon="check"
                                        size="md"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                                    </TailwindButton>
                                </div>
                            </div>
                        </Form>
                    </FormikProvider>
                    )}
                </div>

            </div>
        )}

        {/* Success Snackbar — portal pour échapper au transform de motionify */}
        {createPortal(
            <div className={cn(
                "fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-slate-900 text-white pl-4 pr-3 py-3 rounded-xl shadow-xl transition-all duration-300",
                snackBarOpen
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4 pointer-events-none"
            )}>
                <span className="material-symbols-outlined text-[20px] text-green-400">check_circle</span>
                <span className="text-sm font-medium">Settings saved successfully</span>
                <button
                    onClick={() => setSnackBarOpen(false)}
                    className="ml-1 size-7 rounded-lg hover:bg-white/10 transition-colors flex items-center justify-center"
                >
                    <span className="material-symbols-outlined text-[16px]">close</span>
                </button>
            </div>,
            document.body
        )}
    </>);
}

export { DATESYSTEM_TO_FORMAT }
